import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchQuarterlyResults } from '@apis/fundamentals';
import type {
  QuarterlyResultsApiData,
  QuarterlyResultsForecastType,
} from '@app-types/performanceTrendsApi';
import { getAccessToken } from '@utils/authStorage';
import { getIframeQueryParamsFromSearch } from '@utils/queryParams';

const isQuarterlyForecastType = (
  value: string,
): value is QuarterlyResultsForecastType =>
  value === 'consensus' || value === 'company';

const isValidYears = (value: number): boolean =>
  Number.isFinite(value) && value > 0;

/**
 * React Query hook for the Quarterly Results section. The token only gates the
 * query and is attached by the fundamentals client interceptor.
 */
export const useQuarterlyResults = (
  forecastType: QuarterlyResultsForecastType,
  years: number,
) => {
  const location = useLocation();
  const { ticker, mkt } = getIframeQueryParamsFromSearch(location.search);
  const hasToken = Boolean(getAccessToken());
  const hasValidForecastType = isQuarterlyForecastType(forecastType);
  const hasValidYears = isValidYears(years);

  return useQuery<QuarterlyResultsApiData>({
    queryKey: [
      'fundamentals',
      'quarterly-results',
      ticker,
      mkt,
      forecastType,
      years,
    ],
    queryFn: () => fetchQuarterlyResults({
      ticker: ticker as string,
      mkt: mkt as string,
      forecastType,
      years,
    }),
    enabled: Boolean(
      ticker && mkt && hasToken && hasValidForecastType && hasValidYears
    ),
    staleTime: 60_000,
    retry: 1,
  });
};
